import WebGLTileLayer from 'ol/layer/WebGLTile';
import DataTile from 'ol/source/DataTile';
import ImageTile from 'ol/source/ImageTile';
import { createXYZ } from 'ol/tilegrid';

import {
	createSimilarityExemplar,
	fetchSimilarityTile,
	getSimilarityBackbones,
	getSimilarityFeatureGrid,
	getSimilaritySuggestions,
} from '@utils/api/similarity';
import { similarityMock } from './similarityMock';
import { similarityLayerStyle } from './similarityStyle';

// Development only: VITE_SIMILARITY_MOCK=true runs the tool without the backend
export const isSimilarityMock =
	import.meta.env.DEV && import.meta.env.VITE_SIMILARITY_MOCK === 'true';

export const similarityService = isSimilarityMock
	? similarityMock
	: {
			createExemplar: createSimilarityExemplar,
			getSuggestions: getSimilaritySuggestions,
			// For the hover preview; the mock has no features, so no preview
			getFeatureGrid: getSimilarityFeatureGrid,
			// The mock has one backbone, so no switch
			getBackbones: getSimilarityBackbones,
		};

// Above the orthophoto and analysis layers (from 100), below the hexagon filter (2000)
export const SIMILARITY_Z_INDEX = 150;

let emptyTile = null;

/**
 * The similarity overlay for one exemplar. Threshold, colormap and opacity are applied on the GPU,
 * and tiles are data, so they are never faded in.
 * @param {Object} params
 * @param {string} params.projectUuid
 * @param {string} params.exemplarId
 * @param {string} [params.backbone] - The exemplar's backbone
 * @param {{min: number, max: number}} params.zoomRange - The orthophoto's tile zoom range
 * @param {Object} params.style - From similarityLayerStyle's parameters: colors, threshold, opacity
 * @returns {WebGLTileLayer}
 */
export const createSimilarityLayer = ({ projectUuid, exemplarId, backbone, zoomRange, style }) => {
	const tileGrid = createXYZ({ minZoom: zoomRange.min, maxZoom: zoomRange.max });
	const source = isSimilarityMock
		? new DataTile({
				tileGrid,
				transition: 0,
				loader: (z, x, y) =>
					similarityMock.tileData(exemplarId, tileGrid.getTileCoordExtent([z, x, y])),
			})
		: new ImageTile({
				tileGrid,
				transition: 0,
				loader: async (z, x, y, { signal }) => {
					const tile = await fetchSimilarityTile({
						projectUuid,
						exemplarId,
						backbone,
						z,
						x,
						y,
						signal,
					});
					if (tile) return tile;
					// Outside the orthophoto: a transparent tile
					emptyTile ??= Object.assign(document.createElement('canvas'), {
						width: 256,
						height: 256,
					});
					return emptyTile;
				},
			});
	return new WebGLTileLayer({
		source,
		style: similarityLayerStyle(style),
		zIndex: SIMILARITY_Z_INDEX,
	});
};
